import { useEffect, useRef } from "react";
import { computeTilt } from "./tiltMath";

// Imperative — never triggers a React re-render per pointer move, matching
// this repo's own high-frequency-event convention (see InfiniteCanvas.js's
// scenario-beat camera code). Attach the returned ref to the tiltable
// element; it needs `transform-style: preserve-3d` and a perspective from
// an ancestor (set on the element's own inline style below, no CSS file
// needed) to read correctly in 3D.
//
// Does NOT set `el.style.transition` itself — an inline style assignment
// there would unconditionally replace whatever `transition` shorthand the
// consumer already declared via sx/reveal() (inline style always wins over
// a stylesheet rule, so it would silently kill e.g. reveal()'s opacity
// fade). Callers that want a snappy tilt response should include
// `transform 0.15s ease-out` in their OWN sx-level `transition` declaration
// instead, alongside whatever else they need on that element.
//
// `enabled` (default true): when false, the hook is a no-op — it still
// returns a ref so the caller's JSX doesn't need to branch, but never
// attaches pointer listeners or touches the element's style. Callers should
// pass `enabled: capability.renderCursor` so touch/reduced-motion/mobile
// sessions never get the pointer-tracked tilt (matches the design's fine-
// pointer-only interaction budget).
export function useCardTilt({ maxAngle = 8, enabled = true } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    el.style.transformStyle = "preserve-3d";

    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const { rotateX, rotateY, sheenX, sheenY } = computeTilt(e.clientX, e.clientY, rect, maxAngle);
      el.style.transform = `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
      el.style.setProperty("--tilt-sheen-x", `${sheenX}%`);
      el.style.setProperty("--tilt-sheen-y", `${sheenY}%`);
    };
    const onLeave = () => {
      el.style.transform = "perspective(800px) rotateX(0deg) rotateY(0deg)";
    };

    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerleave", onLeave);
    return () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerleave", onLeave);
    };
  }, [maxAngle, enabled]);

  return ref;
}
